import { ApiService } from "./api.service";
import type { MentionCandidate } from "@/types/mention";

/** The canonical user-summary row the mentionable-users endpoints return. */
interface UserSummary {
  id: string;
  display_name: string;
  avatar_url?: string | null;
}

function toCandidate(user: UserSummary): MentionCandidate {
  return { id: user.id, display_name: user.display_name, avatar_url: user.avatar_url ?? null };
}

/** Fetches the @-mention picker pool for a commentable object — the users who
 *  can read it (issue 1465). One method per object type, each hitting the matching
 *  `…/mentionable-users/` endpoint. */
export class MentionableUsersService extends ApiService {
  async listForProject(slug: string, projectId: string): Promise<MentionCandidate[]> {
    const rows = await this.get<UserSummary[]>(
      `/api/workspaces/${slug}/projects/${projectId}/mentionable-users/`,
    );
    return rows.map(toCandidate);
  }

  async listForWorkItem(
    slug: string,
    projectId: string,
    issueId: string,
  ): Promise<MentionCandidate[]> {
    const rows = await this.get<UserSummary[]>(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/mentionable-users/`,
    );
    return rows.map(toCandidate);
  }

  async listForWorkspaceWorkItem(slug: string, issueId: string): Promise<MentionCandidate[]> {
    const rows = await this.get<UserSummary[]>(
      `/api/workspaces/${slug}/work-items/${issueId}/mentionable-users/`,
    );
    return rows.map(toCandidate);
  }

  async listForSubmission(slug: string, submissionId: string): Promise<MentionCandidate[]> {
    const rows = await this.get<UserSummary[]>(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/mentionable-users/`,
    );
    return rows.map(toCandidate);
  }
}
